import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, fromEvent, Observable, Subject, takeUntil } from 'rxjs';
import { Area } from 'src/app/models/area.model';
import {
  AssetStatus,
  BuildingDetails,
  BuildingStatsOptions,
} from 'src/app/models/building.model';
import { ChecklistItem } from 'src/app/models/checklist-item.model';
import { SortByOptions, UserType } from 'src/app/models/customer.model';
import { KeyValueItem } from 'src/app/models/keyValueItem.model';
import { Region } from 'src/app/models/region.model';
import { PermissionsEnum, SystemComponents } from 'src/app/models/role.model';
import { UserForFilter } from 'src/app/models/user.model';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AreaService } from 'src/app/services/area.service';
import { BuildingService } from 'src/app/services/building.service';
import { PermissionsService, SubGeographicalRegionService } from 'shared-lib';
import { RegionService } from 'src/app/services/region.service';
import { TranslationService } from 'src/app/services/translation.service';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-buildings',
  templateUrl: './buildings.component.html',
  styleUrls: ['./buildings.component.scss'],
})
export class BuildingsComponent implements OnInit, OnDestroy {
  isCinEnabled: boolean = localStorage.getItem('defaultCaimsCodePatterns') === 'false';
  PermissionsEnum = PermissionsEnum;

  buildings: BuildingDetails[] = [];
  stats: KeyValueItem[] = [];

  mapSortBy: Record<number, string> = {
    [SortByOptions.NEWEST]: 'COMMON.SORT_BY.NEWEST',
    [SortByOptions.SLRN_ASC]: 'COMMON.SORT_BY.SLRN_ASC',
    [SortByOptions.SLRN_DESC]: 'COMMON.SORT_BY.SLRN_DESC',
  };
  //filters
  clicked: boolean = false;
  regions: Region[] = [];
  areas: Area[] = [];
  allAreas: Area[] = [];
  validationStatuses: number[] = [];
  anomalyItems: any[] = [];
  buildingHistory: any[] = [];

  statusOptions = [
    {
      name: 'Pending',
      value: AssetStatus.Pending,
      checked: false,
      translation: 'COMMON.ASSET_STATUS.PENDING',
    },
    {
      name: 'Complete',
      value: AssetStatus.Complete,
      checked: false,
      translation: 'COMMON.ASSET_STATUS.COMPLETE',
    },
    {
      name: 'Assigned',
      value: AssetStatus.Assigned,
      checked: false,
      translation: 'COMMON.ASSET_STATUS.ASSIGNED',
    },
    {
      name: 'Rejected',
      value: AssetStatus.Rejected,
      checked: false,
      translation: 'COMMON.ASSET_STATUS.REJECTED',
    },
    {
      name: 'Retagged',
      value: AssetStatus.Retagged,
      checked: false,
      translation: 'COMMON.ASSET_STATUS.RETAGGED',
    },
  ];
  tag?: number;
  tagOptions = [
    { name: 'Tagged', value: 0, translation: 'COMMON.TAG.TAGGED' },
    { name: 'Inaccessible', value: 1, translation: 'COMMON.TAG.INACCESSIBLE' },
  ];
  selectedFilters: ChecklistItem[] = [];

  @Input() dateFrom: string;
  @Input() dateTo: string;

  count: number = 2;
  pageSize: number = 10;
  currentPage: number = 1;

  statsSet: number = 0;

  searchText: string = '';
  searchTextUpdate = new Subject<string>();

  sortByValue: SortByOptions = SortByOptions.NEWEST;
  sortByOptions = SortByOptions;
  filterByValue: string = '';
  sortByName: string = this.mapSortBy[SortByOptions.NEWEST];

  showUpload: boolean = false;
  showDownload: boolean = false;
  downloadBuilding = false;
  downlaodInaccessible = false;
  downloadReplacedMeters = false;

  showSortBy: boolean = false;
  clickObservable: Observable<Event> = fromEvent(document, 'click');
  private ngUnsubscribe = new Subject<void>();
  buName: string = this.translationService.getByKeyFromStorage('BU');
  utName: string = this.translationService.getByKeyFromStorage('UT');
  utilityId: number = 0;
  selectedAreas: number[] = [];
  selectedRegions: number[] = [];

  geoRegions: any[] = [];
  allSubGeoRegions: any[] = [];
  subGeoRegions: any[] = [];
  selectedGeoRegionIds: number[] = [];
  selectedSubgeographicalRegions: number[] = [];

  allCheckboxesChecked: boolean = false;
  dateTypes = [
    {
      name: 'LastUpdateTime',
      value: 'Last Update Time',
      translation: 'COMMON.DATE_TYPE.LAST_UPDATE',
    },
    {
      name: 'TaggedDate',
      value: 'Tagged Date',
      translation: 'COMMON.DATE_TYPE.TAGGED',
    },
    {
      name: 'ValidatedDate',
      value: 'Validated Date',
      translation: 'COMMON.DATE_TYPE.VALIDATED',
    },
    {
      name: 'ReviewDate',
      value: 'Review Date',
      translation: 'COMMON.DATE_TYPE.REVIEW_DATE',
    },
  ];
  selectedDateType: string;
  selectedDateTypeDisplay: string;
  showSelectDateType: boolean = false;
  
  hasCimDataIntegration: boolean = false;

  usersFilter: any; // Stores value of filter
  mobileUsers: UserForFilter[] = []; // Store only mobile users
  adminUsers: UserForFilter[] = []; // Store only admin users
  allUsers: UserForFilter[] = []; // Store mobile or admin users depending on filter
  filteredUsers: UserForFilter[] = []; // Used for display and for filtering users list
  selectedUsers: number[] = [];

  userType?: number;
  userTypeOptions = [
    {
      name: 'First Captured User',
      value: UserType.FirstCapturedUser,
      translation: 'COMMON.USER.TYPE.FIRST_CAPTURED',
    },
    {
      name: 'Last Captured User',
      value: UserType.LastCapturedUser,
      translation: 'COMMON.USER.TYPE.LAST_CAPTURED',
    },
    {
      name: 'Reassigned By',
      value: UserType.ReassignedBy,
      translation: 'COMMON.USER.TYPE.REASSIGNED',
    },
    {
      name: 'Validated By',
      value: UserType.ValidatedBy,
      translation: 'COMMON.USER.TYPE.VALIDATED',
    },
    {
      name: 'Audited By',
      value: UserType.AuditedBy,
      translation: 'COMMON.USER.TYPE.AUDITED',
    },
    {
      name: 'Reviewed By',
      value: UserType.ReviewedBy,
      translation: 'INSTALLATIONS.DETAILS.REVIEWED_BY',
    },
  ];
  mobileUserType: UserType[] = [
    UserType.FirstCapturedUser,
    UserType.LastCapturedUser,
  ];
  adminUserType: UserType[] = [
    UserType.ValidatedBy,
    UserType.ReassignedBy,
    UserType.AuditedBy,
  ];
  navigationExtras?: NavigationExtras;

  dtId?: number;
  dtName?: string;

  dtIds: number[] = [];
  assetName: string = '';

  isListPage: boolean = true;
  editRoute: string = '/buildings/edit';
  reviewStatus?: boolean;

  reviewStatusOptions = [
    {
      name: 'Reviewed',
      value: true,
      translation: 'COMMON.REVIEW_STATUS_OPTIONS.REVIEWED',
    },
    {
      name: 'Not Reviewed',
      value: false,
      translation: 'COMMON.REVIEW_STATUS_OPTIONS.NOT_REVIEWED',
    },
  ];
  isPropertyCodeVisible: boolean = false;

  constructor(
    private buildingService: BuildingService,
    private toastr: ToastrService,
    private regionService: RegionService,
    private areaService: AreaService,
    private translationService: TranslationService,
    public route: ActivatedRoute,
    public permissionsService: PermissionsService,
    private analyticsService: AnalyticsService,
    private userService: UserService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private subGeographicalRegionService: SubGeographicalRegionService
  ) {
    this.navigationExtras = this.router.getCurrentNavigation()?.extras;
  }

  ngOnInit(): void {
    const storedDtIds = localStorage.getItem('dtIds');
    this.hasCimDataIntegration = this.authService.hasCimDataIntegration;
    this.dtIds = storedDtIds ? JSON.parse(storedDtIds) : []; // Parse JSON

    this.assetName = localStorage.getItem('assetName') || '';

    if (this.dtIds.length > 0) {
      this.setNetworkAssetFilter();
      localStorage.removeItem('dtIds');
      localStorage.removeItem('assetName');
    }

    this.dtId = parseInt(localStorage.getItem('dtId') || '0');
    this.dtName = localStorage.getItem('dtName') || '';

    if (this.dtId && this.dtName) {
      this.setDtFilter();
      localStorage.removeItem('dtId');
      localStorage.removeItem('dtName');
    }

    if (this.activatedRoute.snapshot.url[0])
      this.isListPage = this.activatedRoute.snapshot.url[0].path != 'reports';
    this.editRoute = this.isListPage ? '/buildings/edit' : '/buildings/duplicate';
    this.utilityId = parseInt(localStorage.getItem('utilityId') || '');
    this.subscribeToClickEvent();
    this.setChecktValidationStatuses();
    var shouldGetAll = this.setFilters();
    if (this.hasCimDataIntegration) {
      this.getSubGeoRegionsAndDeriveGeo();
    } else {
      this.getRegions();
    }
    this.setCheckedUsers();
    this.getData(shouldGetAll);
    this.searchTextUpdate.pipe(debounceTime(500)).subscribe((value) => {
      if (this.searchText == '') this.search();
    });
    this.selectedDateType = this.selectedDateType ?? this.dateTypes[0].name;
    this.selectedDateTypeDisplay =
      this.selectedDateTypeDisplay ?? this.dateTypes[0].translation;
    this.setFieldsVisibility();
  }
    setChecktValidationStatuses() {
    if (this.validationStatuses) {
       this.statusOptions.forEach((element) => {
        if (this.validationStatuses.includes(element.value)) element.checked = true;
        else element.checked = false;
      });
    }
  }
  ngOnDestroy(): void {
    this.searchTextUpdate.complete();
  }

  setNetworkAssetFilter() {
    if (!localStorage.getItem('filterBuilding')) {
      this.setFilterStorage();
    }
  }

  private getData(getAll: boolean = false): void {
    this.getBuildingStats(getAll);
    this.reloadTable();
  }

  setDtFilter() {
    if (!localStorage.getItem('filterBuilding')) {
      this.setFilterStorage();
    }
  }

  private getBuildingStats(getAllCounts: boolean = false) {
    if (this.isListPage) {
      this.getSingleStat(BuildingStatsOptions.Total, getAllCounts);
      this.getSingleStat(BuildingStatsOptions.Inaccessible, getAllCounts);
      this.getSingleStat(BuildingStatsOptions.Retagged, getAllCounts);
      this.getSingleStat(BuildingStatsOptions.TotalMeters, getAllCounts);
    }
  }

  private getInaccessibleVisitedStats(filter: any) {
    this.buildingService.getBuildingsStats(filter).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          var stat = this.stats.find((x) => x.key == 'Inaccessible');
          var visitedItem = new KeyValueItem();
          visitedItem.friendlyName = 'BUILDINGS.REVISITED_NO';
          visitedItem.isClickable = false;
          visitedItem.key = 'Number of Buildings revisited';
          visitedItem.value = data.data.count;

          if (stat) {
            stat.items = [];
            stat.items.push(visitedItem);
          }
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getStats(filter: any, isGetAll: boolean) {
    this.buildingService.getBuildingsStats(filter).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          var stat = this.stats.find(
            (x) => x.key == data.data.statType?.toString()!
          );
          if (stat) {
            stat.value = data.data.count!;
            this.statsSet++;
            if (
              (isGetAll && this.statsSet == 4) ||
              (!isGetAll && this.statsSet == 3)
            ) {
              this.statsSet = 0;
              this.calculateTaggedCount();
              this.sortCounts(true, filter, isGetAll);
              this.getInaccessibleRevisited(filter.filter, isGetAll);
            }
          } else {
            this.addStat(
              data.data.statType?.toString()!,
              data.data.count!,
              filter,
              isGetAll
            );
          }
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  calculateTaggedCount() {
    var stat = this.stats.find((x) => x.key == 'Tagged');
    var totalItem = this.stats.filter((x) => x.key == 'Total')[0];
    var inaccessibleItem = this.stats.filter((x) => x.key == 'Inaccessible')[0];

    if (stat) {
      stat.value = totalItem.value - inaccessibleItem.value;
    } else {
      var taggedItem = new KeyValueItem();
      taggedItem.key = 'Tagged';
      taggedItem.value = totalItem.value - inaccessibleItem.value;
      taggedItem.order = 2;
      taggedItem.friendlyName = 'COMMON.BUILDING.TYPE.TAGGED';
      this.stats.push(taggedItem);
    }
  }

  private getSingleStat(
    statOption: BuildingStatsOptions,
    getAllCounts: boolean
  ) {
    var filter = {
      filter: this.getFilterObjectForCounts(),
      option: statOption,
      isGetAll: getAllCounts,
    };

    this.getStats(filter, getAllCounts);
  }

  private getInaccessibleRevisited(obj: any, getAllCounts: boolean) {
    var filter = {
      filter: obj,
      option: BuildingStatsOptions.InaccessibleNumberOfRevisits,
      isGetAll: getAllCounts,
    };

    this.getInaccessibleVisitedStats(filter);
  }

  getFilterObjectForCounts() {
    let obj = {
      search: this.searchText,
      filterBy: this.filterByValue,
      userType: this.userType
        ? this.selectedUsers.length > 0
          ? this.userType
          : null
        : null,
      dateFrom: this.dateFrom ? this.dateFrom : null,
      selectedUsers: this.selectedUsers.length > 0 ? this.selectedUsers : null,
      dateTo: this.dateTo ? `${this.dateTo}T23:59:59` : null,
      dateType: this.selectedDateType,
      validationStatuses: this.validationStatuses,
      tag: this.tag != undefined ? (this.tag == 0 ? false : true) : null,
      selectedAreas: this.selectedAreas.length > 0 ? this.selectedAreas : null,
      dtIds: this.dtIds,
      selectedGeoRegions: this.selectedGeoRegionIds.length > 0 ? this.selectedGeoRegionIds : null,
      selectedSubgeographicalRegions: this.selectedSubgeographicalRegions.length > 0 ? this.selectedSubgeographicalRegions : null
    };
    return obj;
  }

  getFilterObject() {
    let obj = {
      sortBy: this.sortByValue,
      search: this.searchText,
      filterBy: this.filterByValue,
      userType: this.userType
        ? this.selectedUsers.length > 0
          ? this.userType
          : null
        : null,
      dateFrom: this.dateFrom ? this.dateFrom : null,
      selectedUsers: this.selectedUsers.length > 0 ? this.selectedUsers : null,
      dateTo: this.dateTo ? `${this.dateTo}T23:59:59` : null,
      dateType: this.selectedDateType,
      validationStatuses: this.validationStatuses,
      tag: this.tag != undefined ? (this.tag == 0 ? false : true) : null,
      selectedAreas: this.selectedAreas.length > 0 ? this.selectedAreas : null,
      selectedRegions:
        this.selectedRegions.length > 0 ? this.selectedRegions : null,
      selectedGeoRegions:
        this.selectedGeoRegionIds.length > 0 ? this.selectedGeoRegionIds : null,
      selectedSubgeographicalRegions:
        this.selectedSubgeographicalRegions.length > 0 ? this.selectedSubgeographicalRegions : null,
      selectedIds: this.buildings
        .filter((x) => x.isChecked == true)
        .map((x) => x.id),
      dtId: this.dtId ? Number(this.dtId) : null,
      dtName: this.dtName ? this.dtName : null,
      dtIds: this.dtIds,
      assetName: this.assetName,
      isListPage: this.isListPage,
      reviewStatus: this.reviewStatus
    };
    return obj;
  }

  async setCheckedUsers() {
    var filter = localStorage.getItem('filterBuilding');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'Building') return;
    if (
      filterObject.filter.filterParams.selectedUsers &&
      filterObject.filter.filterParams.userType
    ) {
      this.userType = filterObject.filter.filterParams.userType;
      this.getUsersForFilter(true, filterObject);
    }
  }

  setFilters() {
    var filter = localStorage.getItem('filterBuilding');
    if (!filter) return true;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'Building') return true;
    this.currentPage = filterObject.filter.pageInfo.page;
    this.pageSize = filterObject.filter.pageInfo.pageSize;
    this.count = filterObject.filter.pageInfo.count;
    this.sortByValue = filterObject.filter.filterParams.sortBy;
    this.searchText = filterObject.filter.filterParams.search;
    this.filterByValue = filterObject.filter.filterParams.filterBy;
    this.dateFrom = filterObject.filter.filterParams.dateFrom;
    this.dateTo = filterObject.filter.filterParams.dateTo;
    this.selectedDateType = filterObject.filter.filterParams.dateType;
    this.selectedDateTypeDisplay =
      this.dateTypes.find(
        (type) => type.name == filterObject.filter.filterParams.dateType
      )?.translation ?? this.dateTypes[0].translation;
    this.validationStatuses = filterObject.filter.filterParams.validationStatuses
      ? filterObject.filter.filterParams.validationStatuses
      : [];
    this.statusOptions.forEach((status) => {
      if(this.validationStatuses.includes(status.value))status.checked = true;  
    });  
    this.selectedUsers = filterObject.filter.filterParams.selectedUsers
      ? filterObject.filter.filterParams.selectedUsers
      : [];
    this.tag =
      filterObject.filter.filterParams.tag != null
        ? filterObject.filter.filterParams.tag
          ? 1
          : 0
        : undefined;
    this.selectedAreas = filterObject.filter.filterParams.selectedAreas
      ? filterObject.filter.filterParams.selectedAreas
      : [];
    this.selectedRegions = filterObject.filter.filterParams.selectedRegions
      ? filterObject.filter.filterParams.selectedRegions
      : [];
    this.selectedGeoRegionIds = filterObject.filter.filterParams.selectedGeoRegions
      ? filterObject.filter.filterParams.selectedGeoRegions
      : [];
    this.selectedSubgeographicalRegions = filterObject.filter.filterParams.selectedSubgeographicalRegions
      ? filterObject.filter.filterParams.selectedSubgeographicalRegions
      : [];
    this.sortByName = this.mapSortBy[this.sortByValue];
    this.dtId = filterObject.filter.filterParams.dtId
      ? filterObject.filter.filterParams.dtId
      : 0;
    this.dtName = filterObject.filter.filterParams.dtName
      ? filterObject.filter.filterParams.dtName
      : undefined;
    this.dtIds = filterObject.filter.filterParams.dtIds;
    this.assetName = filterObject.filter.filterParams.assetName;
    this.reviewStatus = filterObject.filter.filterParams.reviewStatus;

    return false;
  }
  private getAll(obj: any) {
    this.buildingService.getAll(obj).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.buildings = responseData.data.data;
          this.count = responseData.data.count ?? this.count;
          if (responseData.message != '') {
            this.toastr.warning(responseData.message);
          }
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  datesValid() {
    if (this.dateFrom && this.dateTo && this.dateTo?.toString() != '') {
      const valid: boolean =
        new Date(this.dateFrom).getTime() <
        new Date(`${this.dateTo.toString()}T23:59:59`).getTime();
      return valid;
    }

    return true;
  }

  checkAllCheckboxes() {
    this.buildings.forEach((x) => (x.isChecked = !this.allCheckboxesChecked));
    this.allCheckboxesChecked = !this.allCheckboxesChecked;
  }

  checkBuilding(id: number) {
    const building = this.buildings.find((x) => x.id === id);
    if (building) {
      building.isChecked = !building.isChecked;
      this.areAllCheckboxesChecked();
    }
  }

  areAllCheckboxesChecked() {
    this.allCheckboxesChecked = this.buildings.every((x) => x.isChecked);
  }

  onFilterBy(option: any) {
    this.filterByValue = option;
    this.reloadTable(1);
  }

  reloadTable(page: any = null) {
    this.allCheckboxesChecked = false;
    if (page) this.currentPage = page;

    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObject(),
    };

    this.analyticsService.logCustomEvent('BUILDINGS: Filter list', {
      page: obj.pageInfo.page,
      sort_by: obj.filterParams.sortBy,
      search: obj.filterParams.search,
      filter_card: obj.filterParams.filterBy,
      user_type: obj.filterParams.userType,
      date_from: obj.filterParams.dateFrom,
      date_to: obj.filterParams.dateTo,
      users: obj.filterParams.selectedUsers,
      date_type: obj.filterParams.dateType,
      validation_statuses: obj.filterParams.validationStatuses,
      tag: obj.filterParams.tag,
      areas: obj.filterParams.selectedAreas,
      dtId: obj.filterParams.dtId,
    });

    this.getAll(obj);
  }

  search() {
    this.createFilterCards();
  }
  setFilterStorage() {
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObject(),
      utilityId: this.utilityId,
    };

    // Remove 'T23:59:59' from the dateTo to avoid errors
    if (obj.filterParams.dateTo) {
      obj.filterParams.dateTo = obj.filterParams.dateTo.replace(
        'T23:59:59',
        ''
      );
    }

    localStorage.setItem('cameFrom', 'Building');
    localStorage.setItem(
      'filterBuilding',
      JSON.stringify({ type: 'Building', filter: obj })
    );
  }
  sortBy(option: SortByOptions) {
    this.sortByValue = option;
    this.sortByName = this.mapSortBy[option];
    this.reloadTable();
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable();
  }

  private subscribeToClickEvent() {
    this.clickObservable
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((e: any) => {
        this.showUpload = false;
        this.showSortBy = false;
        this.showSelectDateType = false;
      });
  }

  download() {
    if (this.downloadBuilding) {
      this.downloadBuildings();
    } else if (this.downlaodInaccessible) {
      this.downloadInaccessibles();
    } else if (this.downloadReplacedMeters) {
      this.downloadReplacedMeter();
    }
  }
  downloadReplacedMeter() {
    let obj = this.getFilterObject();

    this.analyticsService.logCustomEvent('BUILDINGS: Download Replaced SLRN', {
      sort_by: obj.sortBy,
      search: obj.search,
      filter_card: obj.filterBy,
      user_type: obj.userType,
      date_from: obj.dateFrom,
      date_to: obj.dateTo,
      users: obj.selectedUsers,
      date_type: obj.dateType,
      validation_statuses: obj.validationStatuses,
      tag: obj.tag,
      areas: obj.selectedAreas,
      dtId: obj.dtId,
    });

    this.buildingService.downloadReplacedSlrn(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }
  downloadInaccessibles() {
    let obj = this.getFilterObject();

    this.analyticsService.logCustomEvent(
      'BUILDINGS: Download Inaccessible Meters',
      {
        sort_by: obj.sortBy,
        search: obj.search,
        filter_card: obj.filterBy,
        user_type: obj.userType,
        date_from: obj.dateFrom,
        date_to: obj.dateTo,
        users: obj.selectedUsers,
        date_type: obj.dateType,
        validation_statuses: obj.validationStatuses,
        tag: obj.tag,
        areas: obj.selectedAreas,
      }
    );

    this.buildingService.downloadInaccessible(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }
  downloadBuildings() {
    let obj = this.getFilterObject();

    this.analyticsService.logCustomEvent(
      'BUILDINGS: Download Building Information',
      {
        sort_by: obj.sortBy,
        search: obj.search,
        filter_card: obj.filterBy,
        user_type: obj.userType,
        date_from: obj.dateFrom,
        date_to: obj.dateTo,
        users: obj.selectedUsers,
        date_type: obj.dateType,
        validation_statuses: obj.validationStatuses,
        tag: obj.tag,
        areas: obj.selectedAreas,
        dtId: obj.dtId,
      }
    );

    this.buildingService.download(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }
  selectStatus(e: any) {
    this.createFilterCards();
  }

  selectRegion(e: any) {
    //list of areas for selected regions
    this.areas = this.allAreas.filter((area) =>
      this.regions.some(
        (region) => region.id == area.regionId && region.checked
      )
    );
    this.allAreas.forEach((area) => {
      if (!this.areas.some((a) => a.id === area.id)) {
        area.checked = false; //uncheck areas for unchecked regions
      }
    });

    this.createFilterCards();
  }

  selectArea(e: any) {
    this.createFilterCards();
  }

  selectTag(e: any) {
    if (e.target.value != '') {
      this.tag = e.target.value;
    } else {
      this.tag = undefined;
    }
    this.createFilterCards();
  }

  private getRegions() {
    this.regionService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data;
          this.setCheckedRegions();
          this.getAreas();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getAreas() {
    this.areaService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allAreas = responseData.data;
          this.areas = this.allAreas.filter((area) =>
            this.regions.some(
              (region) => region.id == area.regionId && region.checked
            )
          );
          this.setCheckedAreas();
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  removeFilter(item: ChecklistItem) {
    if (item.property == this.buName) {
      this.regions.forEach((region) => {
        if (region.name === item.selectedValue) {
          region.checked = false;
        }
      });
      this.selectRegion(event);
      this.clicked = !this.clicked;
    } else if (item.property == 'User') {
      this.allUsers.forEach((user) => {
        if (user.name === item.selectedValue) {
          user.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'User type') {
      this.userType = undefined;
      this.allUsers.forEach((user) => {
        // Clear list if he selects same filter again to get all cleared
        user.checked = false;
      });
      this.allUsers = this.filteredUsers = [...[]];
    } else if (item.property == this.utName) {
      this.areas.forEach((area) => {
        if (area.name === item.selectedValue) {
          area.checked = false;
        }
      });
      this.subGeoRegions.forEach((sgr) => {
        if (sgr.name === item.selectedValue) sgr.checked = false;
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Status') {
      this.statusOptions.forEach((status) => {
        if (status.name === item.selectedValue) {
          status.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Tag') {
      this.tag = undefined;
    } else if (item.property == 'Dt Name') {
      this.dtId = 0;
      this.dtName = '';
    } else if (item.property == 'Search') {
      this.searchText = '';
    } else if (item.property == 'Date From') {
      this.dateFrom = '';
    } else if (item.property == 'Date To') {
      this.dateTo = '';
    } else if (item.property == 'Network Asset') {
      this.assetName = '';
      this.dtIds = [];
    } else if (item.property == 'Review Status') {
      this.reviewStatus = undefined;
    }

    this.createFilterCards();

    if (this.selectedFilters.length == 0 && this.dtIds.length == 0) {
      this.regions.forEach((region) => {
        region.checked = false;
      });
      this.reloadTable(1);
      this.getBuildingStats(true);
    }
  }

  resetFilter(e: any) {
    this.selectedFilters = [];

    this.assetName = '';
    this.dtIds = [];

    this.allUsers.forEach((user) => {
      user.checked = false;
    });
    this.allUsers = this.filteredUsers = [...[]];

    this.regions.forEach((region) => {
      region.checked = false;
    });

    this.areas.forEach((area) => {
      area.checked = false;
    });
    this.areas = [];

    this.geoRegions.forEach((g) => { g.checked = false; });
    this.allSubGeoRegions.forEach((s) => { s.checked = false; });
    this.subGeoRegions = [];

    this.selectedAreas = [];
    this.selectedRegions = [];
    this.selectedGeoRegionIds = [];
    this.selectedSubgeographicalRegions = [];

    this.userType = undefined;
    this.statusOptions.forEach((status) => {
      status.checked = false;
    });
    this.validationStatuses = [];
    this.tag = undefined;

    this.clicked = !this.clicked;

    this.dtId = undefined;
    this.dtName = undefined;
    this.searchText = '';
    this.dateFrom = '';
    this.dateTo = '';
    this.reviewStatus = undefined;

    this.reloadTable(1);
    this.getBuildingStats(true);
  }

  createFilterCards() {
    this.selectedFilters = [];

    let selectedUsers = this.allUsers.filter((x) => x.checked);
    let selectedAreas = this.areas.filter((x) => x.checked);
    let selectedStatuses = this.statusOptions.filter((x) => x.checked);
    
    this.selectedAreas = this.areas.filter((x) => x.checked).map((x) => x.id);
    this.selectedUsers = this.allUsers
      .filter((x) => x.checked)
      .map((x) => x.id);
    this.selectedRegions = this.regions
      .filter((x) => x.checked)
      .map((x) => x.id);

    this.selectedGeoRegionIds = this.geoRegions
      .filter((x) => x.checked)
      .map((x) => x.id);
    const selectedSubGeos = this.subGeoRegions.filter((x) => x.checked);
    this.selectedSubgeographicalRegions = selectedSubGeos.map((x) => x.id);

    this.validationStatuses = this.statusOptions
      .filter((x) => x.checked)
      .map((x) => x.value);

    // network asset
    if (this.assetName) {
      this.selectedFilters.push(
        new ChecklistItem(
          'Network Asset',
          this.assetName,
          'COMMON.NETWORKASSETFILTER'
        )
      );
    }

    //areas
    for (var i = 0; i < selectedAreas.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          this.utName,
          selectedAreas[i].name,
          'COMMON.UT.SINGLE'
        )
      );
    }

    //cim sub-geo regions
    for (var i = 0; i < selectedSubGeos.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          this.utName,
          selectedSubGeos[i].name,
          'COMMON.UT.SINGLE'
        )
      );
    }

    //search
    if (this.searchText) {
      this.selectedFilters.push(new ChecklistItem('Search', this.searchText));
    }

    //dates
    if (this.dateFrom) {
      this.selectedFilters.push(new ChecklistItem('Date From', this.dateFrom));
    }

    if (this.dateTo) {
      this.selectedFilters.push(new ChecklistItem('Date To', this.dateTo));
    }

    if (this.userType) {
      var userTypeName = UserType[this.userType!];
      var userTypeTranslationKey = this.userTypeOptions.find(
        (option) => option.value == this.userType
      )?.translation;
      this.selectedFilters.push(
        new ChecklistItem(
          'User type',
          userTypeName,
          'COMMON.USER.TYPE.TITLE',
          userTypeTranslationKey
        )
      );
    }

    //users
    for (var i = 0; i < selectedUsers.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem('User', selectedUsers[i].name, 'COMMON.USER.SINGLE')
      );
    }

    //status
    selectedStatuses.forEach(status => {
      this.selectedFilters.push(
        new ChecklistItem(
          'Status',
          status.name,
          'COMMON.VALIDATION_STATUS',
          status.translation
        )
      );
    })

    //tag
    if (this.tag != undefined) {
      var tagName = this.tagOptions[this.tag].name;
      var tagTranslationKey = this.tagOptions[this.tag].translation;
      this.selectedFilters.push(
        new ChecklistItem('Tag', tagName, 'COMMON.TAG.TITLE', tagTranslationKey)
      );
    }

    //review status
    if (this.reviewStatus != null) {
      let status = this.reviewStatusOptions.filter(
        (x) => x.value == this.reviewStatus
      )[0];
      this.selectedFilters.push(
        new ChecklistItem(
          'Review Status',
          status.name,
          'COMMON.REVIEW_STATUS',
          status.translation
        )
      );
    }

    if (this.dtId && this.dtName) {
      //dt from transformers
      this.selectedFilters.push(
        new ChecklistItem('Dt Name', this.dtName, 'COMMON.DT_NAME')
      );
    }
  }
  setCheckedAreas() {
    var filter = localStorage.getItem('filterBuilding');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'Building') return;
    if (filterObject.filter.filterParams.selectedAreas) {
      this.selectedAreas = filterObject.filter.filterParams.selectedAreas;
      this.areas = this.allAreas.filter((area) =>
        this.regions.some(
          (region) => region.id == area.regionId && region.checked
        )
      );
      this.areas.forEach((element) => {
        if (this.selectedAreas.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
  }
  setCheckedRegions() {
    var filter = localStorage.getItem('filterBuilding');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'Building') return;
    if (filterObject.filter.filterParams.selectedRegions) {
      this.selectedRegions = filterObject.filter.filterParams.selectedRegions;
      this.regions.forEach((element) => {
        if (this.selectedRegions.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
  }
  removeFilterStorage() {
    if (this.hasCimDataIntegration) {
      if (this.allSubGeoRegions.length > 0)
        localStorage.removeItem('filterBuilding');
    } else {
      if (this.allAreas.length > 0 && this.regions.length > 0)
        localStorage.removeItem('filterBuilding');
    }
  }

  private getSubGeoRegionsAndDeriveGeo() {
    this.subGeographicalRegionService.getAllForMappings().subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allSubGeoRegions = (responseData.data ?? []).map((x: any) => ({
            id: x.id,
            name: x.name,
            checked: false,
            geographicalRegionId: x.geographicalRegion?.id,
          }));

          const seen = new Set<number>();
          this.geoRegions = (responseData.data ?? [])
            .filter(
              (x: any) =>
                x.geographicalRegion?.id &&
                !seen.has(x.geographicalRegion.id) &&
                seen.add(x.geographicalRegion.id)
            )
            .map((x: any) => ({
              id: x.geographicalRegion.id,
              name: x.geographicalRegion.name,
              checked: false,
            }));

          this.setCheckedGeoRegions();
          this.rebuildSubGeoRegions();
          this.setCheckedSubGeoRegions();
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status != 204) {
          this.toastr.error(responseData?.message);
        }
      },
      error: () => this.toastr.error('Error occured'),
    });
  }

  private rebuildSubGeoRegions() {
    const checkedGeoIds = this.geoRegions
      .filter((g) => g.checked)
      .map((g) => g.id);
    this.subGeoRegions =
      checkedGeoIds.length > 0
        ? this.allSubGeoRegions.filter((s) =>
            checkedGeoIds.includes(s.geographicalRegionId)
          )
        : [];
    this.allSubGeoRegions.forEach((s) => {
      if (!this.subGeoRegions.some((x) => x.id === s.id)) s.checked = false;
    });
  }

  selectGeoRegion(_: any) {
    this.rebuildSubGeoRegions();
    this.createFilterCards();
  }

  selectSubGeoRegion(_: any) {
    this.createFilterCards();
  }

  setCheckedGeoRegions() {
    const filter = localStorage.getItem('filterBuilding');
    if (!filter) return;
    const filterObject = JSON.parse(filter);
    if (filterObject.type != 'Building') return;
    if (filterObject.filter.filterParams.selectedGeoRegions) {
      this.selectedGeoRegionIds =
        filterObject.filter.filterParams.selectedGeoRegions;
      this.geoRegions.forEach((el) => {
        el.checked = this.selectedGeoRegionIds.includes(el.id);
      });
    }
  }

  setCheckedSubGeoRegions() {
    const filter = localStorage.getItem('filterBuilding');
    if (!filter) return;
    const filterObject = JSON.parse(filter);
    if (filterObject.type != 'Building') return;
    if (filterObject.filter.filterParams.selectedSubgeographicalRegions) {
      this.selectedSubgeographicalRegions =
        filterObject.filter.filterParams.selectedSubgeographicalRegions;
      this.subGeoRegions.forEach((el) => {
        el.checked = this.selectedSubgeographicalRegions.includes(el.id);
      });
    }
  }

  selectDateType(dateType: any) {
    this.selectedDateType = dateType.name;
    this.selectedDateTypeDisplay = dateType.translation;
    this.showSelectDateType = false;
  }

  resetUserList() {
    this.allUsers.forEach((user) => {
      user.checked = false;
    });
    this.allUsers = [];
  }

  selectUserType(e: any) {
    if (this.userType) this.resetUserList();

    if (e.target.value != '') {
      this.userType = e.target.value;
      this.getUsersForFilter(false);
    } else {
      this.userType = undefined;
    }

    this.createFilterCards();
  }

  getUsersForFilter(isFromCache: boolean, filterObject?: any) {
    if (
      this.userType == UserType.FirstCapturedUser ||
      this.userType == UserType.LastCapturedUser
    ) {
      // Dobavi mobilne usere iz baze ako nisi
      if (this.mobileUsers.length === 0) {
        this.userService
          .getUsersForFilter(SystemComponents.Mobile)
          .subscribe((resp) => {
            this.mobileUsers = resp.data;
            this.allUsers = this.filteredUsers = this.mobileUsers;
            if (isFromCache) {
              this.selectedUsers =
                filterObject.filter.filterParams.selectedUsers;
              this.allUsers.forEach((element) => {
                if (this.selectedUsers.includes(element.id))
                  element.checked = true;
                else element.checked = false;
              });
            }
            this.createFilterCards();
          });
      } else {
        this.allUsers = this.filteredUsers = [...this.mobileUsers];
      }
    } else {
      let permission = PermissionsEnum.Building_Assign;

      switch (Number(this.userType)) {
        case UserType.ValidatedBy:
          permission = PermissionsEnum.Building_ApproveReject;
          break;
        case UserType.AuditedBy:
          permission = PermissionsEnum.Building_AuditReview;
          break;
        case UserType.ReviewedBy:
          permission = PermissionsEnum.Building_Review;
          break;
      }

      if (this.adminUsers.length === 0) {
        this.userService
          .getUsersForFilter(SystemComponents.Admin)
          .subscribe((resp) => {
            this.adminUsers = resp.data;
            this.allUsers = this.filteredUsers = this.adminUsers.filter((x) =>
              x.permissions?.some((x) => Number(x) == permission)
            );
            if (isFromCache) {
              this.selectedUsers =
                filterObject.filter.filterParams.selectedUsers;
              this.allUsers.forEach((element) => {
                if (this.selectedUsers.includes(element.id))
                  element.checked = true;
                else element.checked = false;
              });
              this.createFilterCards();
            }
          });
      } else {
        this.allUsers = this.filteredUsers = [
          ...this.adminUsers.filter((x) =>
            x.permissions?.some((x) => Number(x) == permission)
          ),
        ];
      }
    }
  }

  applySearchFilter(e: any) {
    this.filteredUsers = this.allUsers.filter((user) => user.name.toLowerCase().includes(e.toLowerCase()));
  }

  selectUsers(e: any) {
    if (e.status) {
      this.allUsers.push(e.value);
    } else {
      var index = this.allUsers.indexOf(e.value);
      this.allUsers.splice(index, 1);
    }
    this.createFilterCards();
  }

  selectReviewStatus(e: any) {
    this.reviewStatus = e.target.value == 'true';
    this.createFilterCards();
  }

  applyFilter(item: ChecklistItem) {
    this.reloadTable(1);
    this.getBuildingStats();
  }

  private addStat(key: string, value: number, filter: any, isGetAll: boolean) {
    const newItem = new KeyValueItem();
    newItem.key = key;
    newItem.value = value;

    var keyType: BuildingStatsOptions =
      BuildingStatsOptions[key as keyof typeof BuildingStatsOptions];
    switch (keyType) {
      case BuildingStatsOptions.Total:
        newItem.order = 1;
        newItem.friendlyName = 'COMMON.TOTAL';
        break;
      case BuildingStatsOptions.Retagged:
        newItem.order = 3;
        newItem.friendlyName = 'COMMON.ASSET_STATUS.RETAGGED';
        break;
      case BuildingStatsOptions.Inaccessible:
        newItem.order = 4;
        newItem.friendlyName = 'COMMON.BUILDING.TYPE.INACCESSIBLE';
        break;
      case BuildingStatsOptions.TotalMeters:
        newItem.order = 5;
        newItem.friendlyName = 'BUILDINGS.METERS_NO';
        newItem.isClickable = false;
        break;
    }

    this.stats.push(newItem);
    this.sortCounts(false, filter, isGetAll);
  }
  viewAnomaly(id: number) {
    this.buildingService.getBuildingHistory(id).subscribe({
      next: (response) => {
        this.buildingHistory = response?.body?.data ?? [];
       },
    });
  }
  sortCounts(isFromUpdate: boolean, filter: any, isGetAll: boolean) {
    if (isFromUpdate) {
      if (this.stats.length == 5) {
        this.stats.sort((a, b) =>
          a.order < b.order ? -1 : a.order > a.order ? 1 : 0
        );
      }
    } else {
      if (this.stats.length == 4) {
        this.calculateTaggedCount();
        this.getInaccessibleRevisited(filter.filter, isGetAll);
        this.stats.sort((a, b) =>
          a.order < b.order ? -1 : a.order > a.order ? 1 : 0
        );
      }
    }
  }

  setFieldsVisibility() {
    let notVisibleFields: any = localStorage.getItem('notVisibleFields');

    if (!notVisibleFields) {
      return;
    }

    notVisibleFields = JSON.parse(notVisibleFields);
    this.isPropertyCodeVisible = notVisibleFields.find(
      (el: any) => el === 'propertyCode'
    )
      ? false
      : true;
  }
}
